import { useFormik } from "formik";
import * as Yup from "yup";
import "./css/contact.css";
import ReCAPTCHA from "react-google-recaptcha";
import { useRef, useState } from "react";
import { useMedia } from "../../../../hooks/loginHome/useMedia";
import { GetApplicationSetting } from "../../../../assets/configuration/applicationSettings";
import { CommunityClient, ContactFormValues, IActionResponse } from "../../../../services/proxies";
import { RequestAccountDeletionFormValues } from "../../../../services/proxies/account/userClient";
import { useSnackbar } from "../../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../../assets/utils/lib/messageSeverity";


interface ContactProps {
    title?: string;
    formMessageLabel?: string;
    successMessage?: string;
    errorMessage?: string;
    onSubmitForm?: (payload: RequestAccountDeletionFormValues) => Promise<IActionResponse>;
}

export const Contact = (props: ContactProps) => {
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const [captchaKey, setCaptchaKey] = useState(0);
    const { showSnackbar } = useSnackbar();
    const communityClient = new CommunityClient();
    const validationSchema = Yup.object({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        message: Yup.string().required(`${props.formMessageLabel ? props.formMessageLabel : "Message"} is required`),
        recaptcha: Yup.string().required("Please verify that you are not a robot"),
    });

    const defaultSubmit = async (payload: ContactFormValues) => {
        return await communityClient.submitContactForm(payload);
    };

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            subject: "",
            message: "",
            recaptcha: "", 
        },
        validationSchema,
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                const payload = props.formMessageLabel
                    ? {
                        name: values.name,
                        email: values.email,
                        reason: values.message,
                    }
                    : {
                        name: values.name,
                        email: values.email,
                        message: values.message,
                    };

                const result = await (props.onSubmitForm ?? defaultSubmit)(payload);

                if (!result?.isSuccess) {
                    showSnackbar(result?.errorMessages?.[0] || props.errorMessage || "Failed to send message.", MessageSeverity.Error);
                    return;
                }
                showSnackbar(props.successMessage || "Thanks! Your message has been sent.", MessageSeverity.Success);
                resetForm();
            } catch (err) {
                console.error(err);
                showSnackbar(props.errorMessage || "Server error. Please try again.", MessageSeverity.Error);
            } finally {
                // reset the captcha token and Formik field
                recaptchaRef.current?.reset();
                formik.setFieldValue("recaptcha", "");
                setSubmitting(false);
            }
            try {
                showSnackbar(
                    props.successMessage || "Thanks! Your message has been sent.",
                    MessageSeverity.Success
                );
                resetForm();
            } catch (err) {
                console.error(err);
                showSnackbar(
                    props.errorMessage || "Server error. Please try again.",
                    MessageSeverity.Error
                );
            } finally {
                recaptchaRef.current?.reset();
                formik.setFieldValue("recaptcha", "");
                setSubmitting(false);
            }
        },
    });
    const { authSettings } = GetApplicationSetting;
    const siteKey = authSettings.captchaKey ?? "";


    if (!siteKey) {
      console.warn("reCAPTCHA site key missing. Check your .env and prefix.");
    }
    const isXS = useMedia("(max-width: 360px)");
    return (
        <div
            className="content-section section-container"
            style={{
                display: "flex",
                flexDirection: "row",
                padding: "3rem",
                alignItems: "stretch",
                flexWrap: "wrap",
                backgroundColor: !props.title ? "#919191": "#FFFFFF",
            }}
            id="contact"
        >
            <div className="app-container center-text">
                <h1>
                    <span className="in-the-news-title">{props.title ?? "Get in Touch!"}</span>
                </h1>
            </div>

            <div style={{ flex: 1, minWidth: 300 }}>
                <form
                    autoComplete="on"
                    className="react-form-contents"
                    noValidate
                    onSubmit={formik.handleSubmit}
                >
                    <div className="field-list">
                        <div className="form-item field name required">
                            <legend id="name-field-wrapper">
                                <div className="title">
                                    <div>
                                        <span className="label-text">Name</span>
                                        <span className="description required">(required)</span>
                                    </div>
                                </div>
                            </legend>

                            <div className="field full-name">
                                <input
                                    id="name-field"
                                    name="name"
                                    type="text"
                                    className="text-form"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <div className="error-text">{formik.errors.name}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-item field email required">
                            <label htmlFor="email-input" className="title">
                                <div className="">
                                    <span className="label-text">Email</span>
                                    <span className="description required">(required)</span>
                                </div>
                            </label>
                            <input
                                id="email-input"
                                name="email"
                                type="email"
                                className="text-form"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <div className="error-text">{formik.errors.email}</div>
                            )}
                        </div>

                        <div className="form-item field textarea">
                            <label htmlFor="message-textarea" className="title">
                                <div className="">
                                    <span className="label-text">{props.formMessageLabel ?? "Message"}</span>
                                    <span className="description required">(required)</span>
                                </div>
                            </label>
                            <textarea
                                id="message-textarea"
                                name="message"
                                className="text-form"
                                value={formik.values.message}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.message && formik.errors.message && (
                                <div className="error-text">{formik.errors.message}</div>
                            )}
                        </div>
                    </div>

                    <div style={{ margin: "1rem 0" }}>
                        <ReCAPTCHA
                            sitekey={siteKey}
                            onChange={(value) => formik.setFieldValue("recaptcha", value)}
                            size={isXS ? "compact" : "normal"}
                        />
                        {formik.touched.recaptcha && formik.errors.recaptcha && (
                            <div className="error-text">{formik.errors.recaptcha}</div>
                        )}
                    </div>
                    <div className="form-button-wrapper form-button-wrapper--align-left">
                        <button
                            type="submit"
                            className={`button sqs-system-button form-submit-button ${!(formik.isValid && formik.dirty)?"disabled":""}`}
                            disabled={!(formik.isValid && formik.dirty)}
                        >
                            <span className="form-submit-button-state" aria-label="Submit">
                                <span aria-hidden="true">Submit</span>
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
